import { ArrowPathIcon, BeakerIcon, CheckCircleIcon, CloudArrowUpIcon, ExclamationTriangleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';
import Logo from '../../assets/logo-simple.svg?react';
import { Button } from '../../components/Button';
import { StatusPill } from '../../components/Pill';
import { ToolButton } from '../../components/ToolButton';
import { Tooltip } from '../../components/Tooltip';
import { t } from '../../potree/translation/markers';
import { T, useT } from '../../translation/src';
import { ObjectDefinitionStatus } from '../../types/graphqlTypes';
import { HeaderModesBar, type ModeConfig } from '../renderer/ModesBar';
import { useObjectEditor } from './EditorContext';
import { Divider, EditorToolRow, UndoRedo } from './EditorToolbar';
import type { SaveStatus } from './useAutosave';

interface EditorHeaderProps {
  saveStatus: SaveStatus;
  issueCount: number;
  onBack: () => void;
  onPublish: () => void;
  onDiscard: () => void;
  onImport: () => void;
  onMakeSmart: () => void;
  publishing: boolean;
}

/** Edit / Test as the project's mode tabs (the tool row hangs under the active one). */
const EditModeIcon: React.FC = () => <PencilSquareIcon className="h-5 w-5" />;
const TestModeIcon: React.FC = () => <BeakerIcon className="h-5 w-5" />;
const MODES: ModeConfig[] = [
  { id: 'edit', name: t('edit'), icon: EditModeIcon },
  { id: 'test', name: t('test'), icon: TestModeIcon },
];
/** A smart object is only changed through the AI: no Edit mode. */
const SMART_MODES: ModeConfig[] = MODES.filter((mode) => mode.id === 'test');

const SaveIndicator: React.FC<{ status: SaveStatus; issueCount: number }> = ({ status, issueCount }) => {
  const icon = {
    saved: <CheckCircleIcon className="h-4 w-4 text-neon-green-700 dark:text-neon-green-300" />,
    pending: <CloudArrowUpIcon className="h-4 w-4 text-gray-400" />,
    saving: <CloudArrowUpIcon className="h-4 w-4 animate-pulse text-gray-500" />,
    invalid: <ExclamationTriangleIcon className="h-4 w-4 text-warning-yellow-500" />,
    error: <ExclamationTriangleIcon className="h-4 w-4 text-error-red-500" />,
  }[status];
  const label = {
    saved: <T _str="draft saved" swc />,
    pending: <T _str="unsaved changes" swc />,
    saving: <T _str="saving…" swc />,
    invalid: <T _str="fix {count} problems to save" args={{ count: issueCount }} swc />,
    error: <T _str="saving failed, retrying on the next change" swc />,
  }[status];
  return (
    <span className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300" aria-live="polite">
      {icon}
      {label}
    </span>
  );
};

/** Test mode's row: put the test placement back to the default size and params (a smart object: undo / redo too). */
const TestToolRow: React.FC = () => {
  const { definition, setUi } = useObjectEditor();
  const reset = useT('reset the test placement', { swc: true });
  return (
    <div className="flex items-center gap-2">
      <Tooltip delay={0} message={<span>{reset}</span>}>
        <ToolButton icon={ArrowPathIcon} name="reset the test placement" onClick={() => setUi((u) => ({ ...u, test: { params: {} } }))} />
      </Tooltip>
      {definition.smart && (
        <>
          <Divider />
          <UndoRedo />
        </>
      )}
    </div>
  );
};

/**
 * The project header, for an object: logo (back to the library) + object
 * name and state on the left, Edit/Test mode tabs with their tool row in the
 * middle, save state and Publish on the right.
 */
const EditorHeader_: React.FC<EditorHeaderProps> = ({ saveStatus, issueCount, onBack, onPublish, onDiscard, onImport, onMakeSmart, publishing }) => {
  const { definition, ui, setUi } = useObjectEditor();
  const backLabel = useT('back to the object library', { swc: true });
  const isDraft = definition.status === ObjectDefinitionStatus.Draft;
  const hasChanges = isDraft || definition.hasUnpublishedChanges || saveStatus !== 'saved';

  return (
    <div data-panel="header" className="z-20 bg-[rgb(241,243,243)] p-4 dark:bg-[rgb(18,18,18)] dark:text-white">
      <div className="relative flex items-center">
        <div className="flex min-w-0 flex-shrink-0 items-center gap-4">
          <Tooltip message={<span>{backLabel}</span>} delay={0}>
            <Logo className="h-7 flex-shrink-0 cursor-pointer" onClick={onBack} aria-label={backLabel} />
          </Tooltip>
          <div className="h-8 w-px bg-gray-300 dark:bg-gray-600" />
          <div className="min-w-0 max-w-[16rem]">
            <div className="truncate text-sm font-semibold">{definition.name}</div>
            <div className="flex items-center gap-2 text-xs">
              {/* The app's status pills, like the object library's cards. */}
              <StatusPill status={isDraft || definition.status === ObjectDefinitionStatus.Archived ? 'process' : 'pending'} className="text-[11px] font-medium leading-none">
                {isDraft ? <T _str="draft" swc /> : definition.status === ObjectDefinitionStatus.Archived ? <T _str="archived" swc /> : <T _str="published v{version}" args={{ version: definition.activeVersion }} swc />}
              </StatusPill>
            </div>
          </div>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 transform">
          <HeaderModesBar modes={definition.smart ? SMART_MODES : MODES} currentModeId={ui.mode} onModeChange={(mode) => setUi((u) => ({ ...u, mode: mode === 'test' ? 'test' : 'edit' }))}>
            {ui.mode === 'edit' ? <EditorToolRow onImport={onImport} onMakeSmart={onMakeSmart} /> : <TestToolRow />}
          </HeaderModesBar>
        </div>

        <div className="ml-auto flex flex-shrink-0 items-center gap-4">
          <SaveIndicator status={saveStatus} issueCount={issueCount} />
          {!isDraft && definition.hasUnpublishedChanges && (
            <Button variant="secondary" onClick={onDiscard}>
              <T _str="discard changes" swc />
            </Button>
          )}
          <Button variant="primary" onClick={onPublish} loading={publishing} disabled={!hasChanges || issueCount > 0}>
            {isDraft ? <T _str="publish" swc /> : <T _str="publish changes" swc />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EditorHeader = memo(EditorHeader_);
